import { Children, type CSSProperties, type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';

export interface AgendaWindowStackProps {
  children: ReactNode;
  /** Per-layer offset in pixels — [x, y]. Defaults to `[15, 15]`. */
  offset?: readonly [number, number];
  /** Overlay background painted behind every layer. */
  background?: string;
  className?: string;
  style?: CSSProperties;
}

/**
 * Stacks multiple `AgendaWindow` children as cascading modal layers.  Each
 * layer is offset by `offset` so the deepest (back) window peeks up-left
 * above the front, mirroring physical depth.  Back layers are slightly
 * desaturated so the active foreground reads as crisp.
 */
export function AgendaWindowStack({
  children,
  offset = [15, 15] as const,
  background = 'rgba(40, 30, 20, 0.18)',
  className,
  style,
}: AgendaWindowStackProps) {
  const items = Children.toArray(children).filter(Boolean);
  const [ox, oy] = offset;
  const count = items.length;
  const frontTop = 14 + (count - 1) * oy;
  const frontLeft = 18 + (count - 1) * ox;

  return (
    <div
      className={cn('phx-aw-stack relative h-full w-full overflow-hidden', className)}
      style={{ background, ...style }}
    >
      {items.map((child, index) => {
        const depth = count - 1 - index;
        return (
          <div
            key={index}
            className="phx-aw-stack-layer rounded-phx-lg absolute overflow-hidden"
            style={{
              top: frontTop - depth * oy,
              left: frontLeft - depth * ox,
              right: 18,
              bottom: 14,
              boxShadow: '0 12px 32px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.06)',
              filter: depth > 0 ? 'saturate(0.6) brightness(0.985)' : undefined,
              transition: 'top 200ms, left 200ms',
            }}
          >
            {child}
          </div>
        );
      })}
    </div>
  );
}
